<template>
  <div>
    <!-- Hero Section -->
    <hero-section />

    <!-- Featured Teachers Section -->
    <section class="bg-gray-50">
      <div class="container-lg section-padding">
        <div class="text-center mb-12">
          <h2 class="mb-4" data-testid="featured-teachers-title">
            Teachers on Crescendo
          </h2>
          <p class="text-xl text-gray-500 max-w-2xl mx-auto">
            A small and growing group of teachers across {{ region }}.
          </p>
        </div>

        <div v-if="loadingTeachers" class="flex justify-center py-12">
          <!---<svg class="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>-->
        </div>

        <!-- Empty state doubles as teacher recruitment: during soft launch it
             is seen more often than the populated state, and it is also what
             renders if the teacher query fails. -->
        <div
          v-else-if="featuredTeachers.length === 0"
          class="text-center py-12"
          data-testid="featured-teachers-empty"
        >
          <p class="text-gray-500">
            We're onboarding our first teachers now. If you teach in
            {{ region }}, we'd like to hear from you.
          </p>
          <router-link
            to="/for-teachers"
            class="mt-3 inline-block text-primary font-medium underline"
          >
            Teach with Crescendo
          </router-link>
        </div>

        <div
          v-else
          class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
        >
          <teacher-card
            v-for="teacher in featuredTeachers"
            :key="teacher.id"
            :teacher="teacher"
          />
        </div>

        <div class="mt-12 text-center">
          <router-link
            to="/teachers"
            class="btn-primary"
            data-testid="find-teachers-btn"
          >
            View all teachers
          </router-link>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="bg-white">
      <div class="container-lg section-padding">
        <div class="text-center mb-12">
          <h2 class="mb-4">How It Works</h2>
          <p class="text-xl text-gray-500 max-w-2xl mx-auto">
            Getting started with Crescendo is easy. Find, book, and learn — all
            in one place.
          </p>
        </div>

        <div class="grid grid-cols-1 gap-10 md:grid-cols-3">
          <div class="text-center">
            <div
              class="flex items-center justify-center h-16 w-16 rounded-full bg-primary text-white mx-auto mb-6"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <h3 class="text-xl font-semibold mb-3">Find a teacher</h3>
            <p class="text-gray-500">
              Browse teachers in your area and filter by instrument and price.
            </p>
          </div>
          <div class="text-center">
            <div
              class="flex items-center justify-center h-16 w-16 rounded-full bg-primary text-white mx-auto mb-6"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 class="text-xl font-semibold mb-3">Book a lesson</h3>
            <p class="text-gray-500">
              Pick a time from your teacher's live availability and book it
              directly.
            </p>
          </div>
          <div class="text-center">
            <div
              class="flex items-center justify-center h-16 w-16 rounded-full bg-primary text-white mx-auto mb-6"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                />
              </svg>
            </div>
            <h3 class="text-xl font-semibold mb-3">Start learning</h3>
            <p class="text-gray-500">
              Pay securely by card, message your teacher, and get lesson
              resources in one place.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Why We Built This Section -->
    <!-- TODO: surface genuine reviews here once there are enough of them,
         driven by Firestore off the existing teacher-profile review data.
         Never hardcode testimonial markup — the block this replaced was
         entirely fabricated and still referenced a former product name. -->
    <section class="bg-gray-50">
      <div class="container-lg section-padding">
        <div class="text-center mb-8">
          <h2 class="mb-4">Why we built this</h2>
        </div>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto text-center">
          Booking a music lesson still runs on text messages, paper diaries and
          bank transfers that someone has to chase. Crescendo handles the
          scheduling and the payments so teachers can teach and students can
          turn up and play. We're based in Margate and starting with teachers
          across {{ region }}.
        </p>
      </div>
    </section>

    <!-- CTA Section — explicitly two-sided. Students are the primary
         audience, but a teacher arriving from cold outreach needs an
         unambiguous path that isn't a generic signup form. -->
    <section class="bg-primary">
      <div class="container-lg py-12 sm:py-16">
        <!-- Keeps the heading order valid: the columns below are h3s. -->
        <h2 class="sr-only">Get started with Crescendo</h2>
        <div class="grid gap-10 md:grid-cols-2">
          <div>
            <h3
              class="text-2xl font-bold tracking-tight text-white sm:text-3xl"
            >
              Looking for lessons?
            </h3>
            <p class="mt-4 text-lg text-white">
              Browse teachers across {{ region }}.
            </p>
            <router-link
              to="/teachers"
              class="btn-lg bg-white text-primary hover:bg-gray-100 mt-6 inline-block"
              data-testid="find-teachers-cta-btn"
            >
              Find a teacher
            </router-link>
          </div>
          <div>
            <h3
              class="text-2xl font-bold tracking-tight text-white sm:text-3xl"
            >
              Teach music?
            </h3>
            <p class="mt-4 text-lg text-white">
              We're looking for {{ foundingCohortSize }} founding teachers in
              {{ region }}. No commission until {{ foundingOfferEndDate }}.
            </p>
            <router-link
              to="/for-teachers"
              class="btn-lg bg-white text-primary hover:bg-gray-100 mt-6 inline-block"
              data-testid="for-teachers-cta-btn"
            >
              Read more
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { getAllTeachers } from "@/services/teacher.service";
import TeacherCard from "@/components/TeacherCard.vue";
import HeroSection from "@/components/HeroSection.vue";
import { LAUNCH, foundingOfferEndDateLabel } from "@/config/launch";

export default {
  name: "HomeView",
  components: {
    "teacher-card": TeacherCard,
    "hero-section": HeroSection,
  },
  setup() {
    const featuredTeachers = ref([]);
    const loadingTeachers = ref(true);

    const fetchFeaturedTeachers = async () => {
      try {
        loadingTeachers.value = true;

        // Get top 3 highest-rated teachers
        const teachers = await getAllTeachers({ limit: 3 });

        // Ensure we have all the required properties for TeacherCard
        featuredTeachers.value = teachers.map((teacher) => ({
          id: teacher.id || "",
          name: teacher.name || "Unknown Teacher",
          instrument: teacher.instrument || "Music",
          bio: teacher.bio || "No bio available",
          hourlyRate: teacher.hourlyRate || 0,
          rating: teacher.rating || 5,
          reviewCount: teacher.reviewCount || 0,
          photoURL: teacher.photoURL || "",
          tags: teacher.tags || [],
        }));
      } catch (error) {
        console.error("Error fetching featured teachers:", error);
        featuredTeachers.value = []; // Set empty array on error
      } finally {
        loadingTeachers.value = false;
      }
    };

    // No fallback teacher data: a visitor hitting a transient Firestore error
    // must not be shown invented teachers on a page that presents them as real.
    // fetchFeaturedTeachers already leaves the list empty on failure, which
    // falls through to the empty state above.
    onMounted(async () => {
      await fetchFeaturedTeachers();
    });

    return {
      featuredTeachers,
      loadingTeachers,
      region: LAUNCH.region,
      foundingCohortSize: LAUNCH.foundingCohortSize,
      foundingOfferEndDate: foundingOfferEndDateLabel(),
    };
  },
};
</script>
